<template>
  <span
    v-if="mention.audience"
    class="no-wrap flex"
  >
    <div
      class="row"
      data-testid="audience-reach-trigger"
      @mouseout="popperVisible = false"
      @mouseover="popperVisible = true"
    >
      <div class="tw:flex tw:flex-nowrap">
        <div
          :class="
            dense ? 'tw:mr-0.5 tw:pt-[1px]' : 'tw:ml-[5px] tw:mr-2 tw:pt-1.5'
          "
        >
          <BaseIcon
            class="text-ds-denim-9"
            icon="followers"
            :style="{ 'font-size': dense ? '21px' : '18px' }"
          />
        </div>

        <div
          class="c-potential-audience-reach__label"
          :class="dense ? 'tw:text-xxs' : 'tw:text-base tw:leading-5'"
        >
          {{ $t("potential_audience_reach_widget.short_label") }}

          <div
            class="tw:text-left tw:font-bold"
            :class="{ 'tw:text-xxs': dense }"
          >
            {{ formattedNumber(mention.audience) }}
          </div>
        </div>
      </div>
    </div>

    <BasePopper
      v-if="isCisionOne && isOnlineMention"
      v-model="popperVisible"
      caret
      class="tw:rounded-md tw:bg-white tw:shadow-[10px_10px_10px_10px_rgba(0,0,0,0.1)]"
      flip
      :offset="[0, 10]"
      tether
    >
      <div class="tw:flex tw:flex-row tw:p-2">
        <div
          class="tw:flex tw:flex-col"
          data-testid="online-audience-breakdown"
        >
          <span class="tw:mb-2 tw:text-denim-900">
            {{ $t("potential_audience_reach_widget.total_reach") }}
          </span>

          <div class="tw:text-blue-600">
            <span class="tw:text-left tw:font-bold">
              {{ formattedNumber(mention.mobile_monthly_visits) }}
            </span>

            {{ $t("potential_audience_reach_widget.mobile") }}
          </div>

          <div class="tw:text-blue-800">
            <span class="tw:text-left tw:font-bold">
              {{ formattedNumber(mention.desktop_monthly_visits) }}
            </span>

            {{ $t("potential_audience_reach_widget.desktop") }}
          </div>
        </div>

        <PotentialAudienceReachCircularProgress
          class="circular-progress"
          data-testid="audience-reach-chart"
          :percentage="percentage"
        >
          <template #text>
            <div
              class="text tw:absolute tw:top-6 tw:text-center tw:font-bold"
              data-testid="audience-total"
            >
              {{ formattedNumber(mention.audience) }}
            </div>
          </template>
        </PotentialAudienceReachCircularProgress>
      </div>
    </BasePopper>

    <BaseStreemTooltip v-else>
      <div class="q-pa-sm">
        <MetricEditorInfoAlert
          class="q-mb-sm"
          show-audience-multiplier-info
        />

        <div>
          {{
            $t("print_mention_card.potential_audience_reach", {
              audience: formattedNumber(mention.audience),
            })
          }}
        </div>
      </div>
    </BaseStreemTooltip>
  </span>
</template>

<script>
import { storeToRefs } from "pinia";

import {
  BaseIcon,
  BasePopper,
  BaseStreemTooltip,
} from "shared/components/base";
import PotentialAudienceReachCircularProgress from "shared/components/PotentialAudienceReachCircularProgress.vue";
import MetricEditorInfoAlert from "shared/components/user/MetricEditorInfoAlert.vue";
import { suffix } from "shared/helpers/number";
import { useGlobalStore } from "shared/stores/global";
import { isOnlineArticle } from "shared/types/mentions/guards";

export default {
  name: "PotentialAudienceReachWidget",
  components: {
    BaseIcon,
    PotentialAudienceReachCircularProgress,
    MetricEditorInfoAlert,
    BasePopper,
    BaseStreemTooltip,
  },
  props: {
    mention: {
      type: Object,
      required: true,
    },
    dense: {
      type: Boolean,
    },
  },
  setup() {
    const globalStore = useGlobalStore();
    const { isCisionOne } = storeToRefs(globalStore);

    return {
      isCisionOne,
    };
  },
  data() {
    return {
      popperVisible: false,
    };
  },
  computed: {
    isOnlineMention() {
      return isOnlineArticle(this.mention);
    },
    percentage() {
      return (
        (this.mention.desktop_monthly_visits / this.mention.audience) * 100
      );
    },
  },
  methods: {
    formattedNumber(audience) {
      return suffix(Number(audience));
    },
  },
};
</script>

<style lang="scss" scoped>
// VPAT-43: the label inherits $soft (#767676) = 4.2:1 on the #f5f7f9
// (denim-1) hover surface, failing WCAG 1.4.3. The subtext token
// (--s-color-font-subtext = denim-7) is only 4.1:1 there, so step to
// denim-8 (5.85:1) — scoped to this widget, not the global $soft token.
.c-potential-audience-reach__label {
  color: var(--s-color-denim-8);
}

.circular-progress {
  width: 78px;
  height: 62px;

  .text {
    width: 90px;
  }
}
</style>
